import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LandingHeader from './LandingHeader';
import Footer from '../legal/Footer';
import CookieBanner from '../legal/CookieBanner';
import { OptimizedSchoolRegistrationWizard } from '../ui/OptimizedComponents';
import { SkipLink } from '../ui/AccessibilityComponents';
import AuraLightBackground from '../ui/AuraLightBackground';
import SEO from '../seo/SEO';
import usePublicPageLayout from '../../hooks/usePublicPageLayout';
import HeroSection from './site/HeroSection';

// Unter dem Hero: eigener Chunk, laedt direkt nach dem ersten Bild.
const BelowFold = lazy(() => import(/* webpackChunkName: "landing-sections" */ './site/BelowFold'));

/**
 * Landingpage (Relaunch Oktober 2026). Sieben Bloecke, jeder genau einmal:
 *   1 Hero · 2 So geht's · 3 Schulnetz · 4 Fuer Schulen · 5 Wer dahinter steht
 *   6 Pilot + Kontakt · 7 Footer
 * Der Hero rendert sofort; die Sektionen darunter kommen als eigener Chunk
 * direkt danach (BelowFold), die Bewegung (Motion-Kit + GSAP) erst nach dem
 * load-Ereignis, siehe hooks/useMotionKit.js.
 *
 * Anker von aussen: /#so-gehts, /#schulnetz, /#fuer-schulen, /#pilot,
 * /#contact (Footer, FAQ) und /#schule-registrieren (oeffnet den Wizard; Link
 * aus AuthPage).
 */
const HEADER_OFFSET = 72;

const LandingPage = () => {
  const [wizardOpen, setWizardOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  usePublicPageLayout();

  const openWizard = useCallback(() => setWizardOpen(true), []);
  const closeWizard = useCallback(() => setWizardOpen(false), []);

  // Hash-Navigation (/#contact aus FAQ und Footer, „So geht's“ im Header).
  // Robust gegen eine Seite, die waehrend des Scrollens waechst: Die Sektionen
  // laden als Chunk nach, Kit-Szenen bauen sich unterwegs auf, und deren
  // ScrollTrigger.refresh() bricht laufendes Smooth-Scrolling ab. Deshalb:
  // - von einer anderen Seite kommend direkt springen, auf der Seite sanft;
  // - bis zu 4 s nachkorrigieren, wenn das Ziel nicht oben steht (Scroll
  //   abgebrochen oder Ziel verrutscht);
  // - sofort aufhoeren, sobald jemand selbst scrollt oder eine Taste drueckt.
  const firstHashRef = useRef(true);
  useEffect(() => {
    const id = decodeURIComponent((location.hash || '').replace('#', ''));
    const firstVisit = firstHashRef.current;
    firstHashRef.current = false;
    if (!id) return undefined;
    if (id === 'schule-registrieren') {
      setWizardOpen(true);
      navigate(location.pathname, { replace: true });
      return undefined;
    }

    let stopped = false;
    const timers = [];
    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    const stop = () => { stopped = true; };
    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    userEvents.forEach((e) => window.addEventListener(e, stop, { passive: true }));

    const offset = (el) => el.getBoundingClientRect().top - HEADER_OFFSET;
    const go = (el, behavior) => window.scrollTo({ top: Math.max(0, window.scrollY + offset(el)), behavior });

    const started = Date.now();
    let lastY = -1;
    const settle = () => {
      if (stopped) return;
      const el = document.getElementById(id);
      if (!el) return;
      const off = Math.abs(offset(el));
      const stalled = Math.abs(window.scrollY - lastY) < 2;
      lastY = window.scrollY;
      // Steht das Ziel oben: fertig — aber noch ein paar Mal pruefen, ob es verrutscht.
      if (off <= 6) { if (Date.now() - started < 4000) later(settle, 400); return; }
      if (stalled) go(el, 'auto');
      if (Date.now() - started < 4000) later(settle, 250);
    };

    let attempts = 0;
    const first = () => {
      if (stopped) return;
      const el = document.getElementById(id);
      if (!el) {
        if (attempts < 30) { attempts += 1; later(first, 100); }
        return;
      }
      go(el, firstVisit ? 'auto' : 'smooth');
      later(settle, firstVisit ? 200 : 600);
    };
    later(first, 60);

    return () => {
      stop();
      timers.forEach(clearTimeout);
      userEvents.forEach((e) => window.removeEventListener(e, stop));
    };
  }, [location.hash, location.key, location.pathname, navigate]);

  return (
    <div className="relative min-h-screen bg-background">
      <SEO
        title="each otter – Peer-Learning für Schulen | EachOtter"
        description="each otter ist die Peer-Learning-App für Schulen: Schüler:innen finden an ihrer Schule, wer ihnen weiterhilft, und verabreden sich zum Lernen. Let's help each otter!"
        canonical="https://eachotter.de/"
        image="https://eachotter.de/og-image-v2.jpg"
      />
      <SkipLink targetId="main-content">Zum Hauptinhalt springen</SkipLink>
      <AuraLightBackground intensity="normal" className="z-0" />

      <LandingHeader />

      <main id="main-content" tabIndex={-1} className="relative outline-none">
        <HeroSection onApply={openWizard} />
        <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
          <BelowFold onApply={openWizard} />
        </Suspense>
      </main>

      <Footer />

      <OptimizedSchoolRegistrationWizard isOpen={wizardOpen} onClose={closeWizard} />
      <CookieBanner />
    </div>
  );
};

export default LandingPage;
